import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, FileSpreadsheet, FileText, Loader2, Wallet } from 'lucide-react';
import Alert from '../common/Alert';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import ListPageHeader from '../common/ListPageHeader.js';
import ListPagination from '../common/ListPagination.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import SelectField from '../common/SelectField.js';
import SettlementFilterBar from '../common/SettlementFilterBar.js';
import SettlementModalShell from '../common/SettlementModalShell.js';
import CompanySettlementBatchView from '../common/CompanySettlementBatchView.js';
import PaymentProofModal from '../common/PaymentProofModal.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { filterDateFieldClass, filterExportButtonClass, filterExportPairClass, formInputClass, formSelectClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import { paginateArray } from '../../utils/clientPaginate.js';
import { formatIstDateTime, getCurrentMonthRange } from '../../utils/localDate.js';
import { formatHubBranchLabel } from '../../utils/destinationType.js';
import companySettlementService from '../../services/companySettlementService.js';
import mlAgentCommissionService from '../../services/mlAgentCommissionService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { PAYMENT_MODES, statusBadge, statusLabel } from '../paid-amount-settlement/paidAmountSettlementUi.js';

const TABS = [
  { id: 'pending', label: 'Pending', statusParam: 'OCR_PENDING,HUMAN_REVIEW,PENDING' },
  { id: 'approved', label: 'Approved', statusParam: 'APPROVED' },
  { id: 'rejected', label: 'Rejected', statusParam: 'REJECTED' },
  { id: 'company_paid', label: 'Company paid', statusParam: '' },
];

const dateParams = (fromDate, toDate) => {
  const params = {};
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  return params;
};

const canReview = (status) => status === 'HUMAN_REVIEW' || status === 'PENDING';
const OWES_PAGE_SIZE = 10;

const AccountantCompanySettlementApprovals = () => {
  const [searchParams] = useSearchParams();
  const focusBatchId = (searchParams.get('batch_id') || searchParams.get('batch') || '').trim();
  const openedBatchRef = useRef('');
  const [tab, setTab] = useState('pending');
  const [fromDate, setFromDate] = useState(() => getCurrentMonthRange().fromDate);
  const [toDate, setToDate] = useState(() => getCurrentMonthRange().toDate);
  const [hubId, setHubId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [hubOptions, setHubOptions] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [companyPayments, setCompanyPayments] = useState([]);
  const [owesAgents, setOwesAgents] = useState([]);
  const [owesPage, setOwesPage] = useState(1);
  const [payTarget, setPayTarget] = useState(null);
  const [proofView, setProofView] = useState(null);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, all: 0 });
  const [aggregates, setAggregates] = useState({
    due_to_company: 0,
    paid_due: 0,
    to_pay_due: 0,
    paid_to_pay_due: 0,
    remitted_approved: 0,
    pending_review_amount: 0,
    pending_review_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [viewBatch, setViewBatch] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payMode, setPayMode] = useState('cash');
  const [payRemarks, setPayRemarks] = useState('');
  const [payReceipt, setPayReceipt] = useState(null);
  const payAfterSelect = useRef(false);

  const presetAgent = (searchParams.get('agent_user_id') || '').trim();
  useEffect(() => {
    if (presetAgent) setAgentId(presetAgent);
  }, [presetAgent]);

  const statusParam = TABS.find((t) => t.id === tab)?.statusParam || '';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await companySettlementService.listBatches({
        status: statusParam,
        ...dateParams(fromDate, toDate),
        ...(hubId ? { location_hub_id: hubId } : {}),
        ...(agentId ? { agent_user_id: agentId } : {}),
      });
      setRows(res?.data || []);
      setCompanyPayments(res?.company_payments || []);
      setOwesAgents(res?.company_owes_agents || []);
      setOwesPage(1);
      const nextCounts = res?.counts || { pending: 0, approved: 0, rejected: 0, all: 0 };
      setCounts({
        ...nextCounts,
        company_paid: (res?.company_payments || []).length,
      });
      const ag = res?.aggregates || {};
      const listed = res?.data || [];
      const approvedSum = listed
        .filter((r) => String(r.status || '').toUpperCase() === 'APPROVED')
        .reduce((sum, r) => sum + Number(r.total_amount || 0), 0);
      const pendingListed = listed.filter((r) => {
        const status = String(r.status || '').toUpperCase();
        return status && status !== 'APPROVED' && status !== 'REJECTED';
      });
      const pendingSum = pendingListed.reduce((sum, r) => sum + Number(r.total_amount || 0), 0);
      setAggregates({
        due_to_company: Number(ag.due_to_company || 0),
        paid_due: Number(ag.paid_due || 0),
        to_pay_due: Number(ag.to_pay_due || 0),
        paid_to_pay_due: Number(ag.paid_to_pay_due || 0),
        remitted_approved: Number(ag.remitted_approved || 0) || (tab === 'approved' ? approvedSum : 0),
        pending_review_amount: Number(ag.pending_review_amount || 0) || (tab === 'pending' ? pendingSum : 0),
        pending_review_count: Number(ag.pending_review_count ?? nextCounts.pending ?? pendingListed.length ?? 0),
      });
      const optAgents = res?.filter_options?.agents || [];
      const optHubs = res?.filter_options?.hubs || [];
      setAgentOptions((prev) => {
        const byId = new Map(prev.map((agent) => [String(agent.user_id), agent]));
        optAgents.forEach((agent) => {
          if (agent?.user_id) byId.set(String(agent.user_id), agent);
        });
        return Array.from(byId.values()).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      });
      if (payAfterSelect.current && agentId && Number(ag.due_to_company) < 0) {
        payAfterSelect.current = false;
        setPayMode('cash');
        setPayRemarks('');
        setPayReceipt(null);
        setPayOpen(true);
      }
      setHubOptions((prev) => {
        const byId = new Map(prev.map((h) => [String(h.id), h]));
        optHubs.forEach((h) => {
          if (h?.id != null) byId.set(String(h.id), { id: h.id, name: h.name });
        });
        return Array.from(byId.values());
      });
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load settlements');
    } finally {
      setLoading(false);
    }
  }, [statusParam, fromDate, toDate, hubId, agentId, tab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await locationHubService.getHubsForSelect({ brief: 1 });
        const list = res?.data?.hubs || res?.data || res?.hubs || [];
        const mapped = (Array.isArray(list) ? list : []).map((h) => ({
          id: h.id,
          name: formatHubBranchLabel(h) || h.location || h.name,
        })).filter((h) => h.id != null);
        if (!cancelled && mapped.length) {
          setHubOptions((prev) => {
            const byId = new Map(mapped.map((h) => [String(h.id), h]));
            prev.forEach((h) => {
              if (!byId.has(String(h.id))) byId.set(String(h.id), h);
            });
            return Array.from(byId.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
          });
        }
      } catch {
        /* keep options from settlement batches */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onApprove = async (batchId) => {
    try {
      setBusyId(batchId);
      const res = await companySettlementService.approveBatch(batchId);
      Alert.success(res?.message || 'Approved');
      setViewBatch(null);
      await loadData();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Approve failed');
    } finally {
      setBusyId('');
    }
  };

  const onReject = (batchId) => {
    setOpenMenuId(null);
    setRejectNotes('');
    setRejectTarget({ batchId });
  };

  const closeReject = () => {
    if (busyId) return;
    setRejectTarget(null);
    setRejectNotes('');
  };

  const confirmReject = async () => {
    if (!rejectTarget?.batchId) return;
    const batchId = rejectTarget.batchId;
    try {
      setBusyId(batchId);
      const res = await companySettlementService.rejectBatch(batchId, {
        review_notes: rejectNotes.trim(),
      });
      Alert.success(res?.message || 'Settlement rejected');
      setRejectTarget(null);
      setRejectNotes('');
      setViewBatch(null);
      await loadData();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Reject failed');
    } finally {
      setBusyId('');
    }
  };

  const exportParams = {
    status: statusParam,
    ...dateParams(fromDate, toDate),
    ...(hubId ? { location_hub_id: hubId } : {}),
    ...(agentId ? { agent_user_id: agentId } : {}),
  };

  const downloadExcel = async () => {
    try {
      await companySettlementService.downloadExcel(exportParams);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Excel download failed');
    }
  };

  const downloadPdf = async () => {
    try {
      await companySettlementService.downloadPdf(exportParams);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'PDF download failed');
    }
  };

  const openView = async (row) => {
    try {
      const res = await companySettlementService.getBatch(row.batch_id);
      setViewBatch(res?.data || row);
    } catch (err) {
      setViewBatch(row);
    }
  };

  useEffect(() => {
    if (!focusBatchId || loading || openedBatchRef.current === focusBatchId) return undefined;
    openedBatchRef.current = focusBatchId;
    const match = rows.find((row) => row.batch_id === focusBatchId);
    (async () => {
      if (match) {
        await openView(match);
        return;
      }
      try {
        const res = await companySettlementService.getBatch(focusBatchId);
        if (res?.data) {
          const status = String(res.data.status || '').toUpperCase();
          if (status === 'APPROVED') setTab('approved');
          else if (status === 'REJECTED') setTab('rejected');
          else setTab('pending');
          setViewBatch(res.data);
        }
      } catch {
        openedBatchRef.current = '';
      }
    })();
    return undefined;
  }, [focusBatchId, loading, rows]);

  const totalCount = counts.all ?? (counts.pending + counts.approved + counts.rejected);
  const tabLabel = TABS.find((item) => item.id === tab)?.label || 'This tab';
  const companyOwes = Number(aggregates.due_to_company) < 0;
  const companyOwesAmount = Math.abs(Number(aggregates.due_to_company) || 0);
  const payToAgents = owesAgents.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const selectedAgentName = (
    agentOptions.find((agent) => String(agent.user_id) === String(agentId)) || {}
  ).name || '';
  const owesView = paginateArray(owesAgents, owesPage, OWES_PAGE_SIZE);
  const owesPageCount = owesView.totalPages;
  const owesPageSafe = owesView.currentPage;
  const owesSlice = owesView.items;
  const openPayFor = (row) => {
    if (!row?.user_id || !(Number(row.amount) > 0)) {
      Alert.error('Select an agent the company owes');
      return;
    }
    const lines = Array.isArray(row.lines) ? row.lines : [];
    setPayTarget({
      user_id: String(row.user_id),
      name: row.name || '',
      amount: Number(row.amount) || 0,
      lines,
      entry_ids: lines.map((line) => line.id).filter((id) => id != null),
    });
    setPayMode('cash');
    setPayRemarks('');
    setPayReceipt(null);
    setPayOpen(true);
  };
  const openPay = () => {
    if (!agentId) {
      Alert.error('Select the agent in the Agent filter, then pay');
      return;
    }
    openPayFor({
      user_id: agentId,
      name: selectedAgentName,
      amount: companyOwesAmount,
    });
  };

  const firstRequest = (row) =>
    (row?.paid_requests && row.paid_requests[0]) || (row?.to_pay_requests && row.to_pay_requests[0]) || {};

  const reviewerLabel = (row) => {
    const nested = firstRequest(row);
    const label =
      row.approved_by_display ||
      row.approved_by_name ||
      nested.reviewed_by_display ||
      nested.reviewed_by_name ||
      '';
    return label && label !== '—' ? label : '';
  };

  const approvedAtValue = (row) => row.approved_at || firstRequest(row).reviewed_at;

  return (
    <DashboardLayout>
      <ListPageHeader
        title="Settlement Approvals"
        subtitle="Approve what the agent sends. If the company owes the agent, pay them here with a payment proof. The Daily Branch Report picks up Payment received and Payment paid."
        icon={Check}
        backTo="/accountant-dashboard"
      />

      <SettlementFilterBar
        statusLabel="Status"
        tabs={TABS}
        activeTab={tab}
        onTabChange={setTab}
        counts={counts}
        fromDate={fromDate}
        toDate={toDate}
        onFromDate={setFromDate}
        onToDate={setToDate}
        centerStatus
        extraLeft={(
          <div className="grid grid-cols-1 sm:grid-cols-[11rem_16rem] gap-2 sm:gap-3 w-full lg:w-auto lg:flex lg:flex-wrap">
            <div className={filterDateFieldClass}>
              <SelectField
                variant="filter"
                label="Hub / Branch"
                value={hubId}
                onChange={(e) => setHubId(e.target.value)}
                placeholder="All hubs"
                emptyLabel="All hubs"
                options={[
                  { value: '', label: 'All hubs' },
                  ...hubOptions.map((h) => ({ value: String(h.id), label: h.name })),
                ]}
              />
            </div>
            <div className="min-w-0 w-full sm:w-64">
              <SelectField
                variant="filter"
                label="Agent"
                value={agentId}
                onChange={(e) => {
                  const next = e.target.value;
                  setAgentId(next);
                  payAfterSelect.current = Boolean(next);
                  if (!next) setPayOpen(false);
                }}
                placeholder="All agents"
                emptyLabel="All agents"
                options={[
                  { value: '', label: 'All agents' },
                  ...agentOptions.map((a) => {
                    const hub = String(
                      a.location_hub_name
                      || hubOptions.find((item) => String(item.id) === String(a.location_hub_id))?.name
                      || '',
                    ).trim();
                    const name = a.name || a.user_id;
                    return {
                      value: String(a.user_id),
                      label: hub ? `${name} (${hub})` : name,
                    };
                  }),
                ]}
              />
            </div>
          </div>
        )}
        extraRight={(
          <div>
            <label className="hidden lg:block text-xs font-medium text-transparent mb-1 select-none">Export</label>
            <div className={filterExportPairClass}>
              <Button variant="secondary" className={filterExportButtonClass} onClick={downloadExcel}>
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
              </Button>
              <Button variant="secondary" className={filterExportButtonClass} onClick={downloadPdf}>
                <FileText className="w-4 h-4 mr-1" /> PDF
              </Button>
            </div>
          </div>
        )}
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <div className={`bg-white rounded-xl border p-3 sm:p-4 ${Number(aggregates.due_to_company) < 0 ? 'border-emerald-100' : 'border-amber-100'}`}>
          <p className="text-xs text-gray-500">
            {Number(aggregates.due_to_company) < 0 ? 'Company owes agent' : 'Due to company'}
          </p>
          <p className={`text-lg sm:text-xl font-semibold tabular-nums ${Number(aggregates.due_to_company) < 0 ? 'text-emerald-800' : 'text-amber-800'}`}>
            {fullCurrency(Math.abs(Number(aggregates.due_to_company) || 0))}
          </p>
          {Number(aggregates.due_to_company) < 0 && agentId && (
            <p className="text-[11px] text-emerald-700 mt-1">Pay from the button below</p>
          )}
        </div>
        <div className="bg-white rounded-xl border border-sky-100 p-3 sm:p-4">
          <p className="text-xs text-gray-500">Paid / To-Pay due</p>
          <p className="text-lg sm:text-xl font-semibold text-sky-800 tabular-nums">
            {fullCurrency(aggregates.paid_to_pay_due)}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">
            Paid {fullCurrency(aggregates.paid_due)}
            <span className="text-gray-300"> · </span>
            To-Pay {fullCurrency(aggregates.to_pay_due)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-emerald-100 p-3 sm:p-4">
          <p className="text-xs text-gray-500">Remitted (approved)</p>
          <p className="text-lg sm:text-xl font-semibold text-emerald-800 tabular-nums">
            {fullCurrency(aggregates.remitted_approved)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4">
          <p className="text-xs text-gray-500">Pending review</p>
          <p className="text-lg sm:text-xl font-semibold text-gray-800 tabular-nums">
            {fullCurrency(aggregates.pending_review_amount)}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">
            {aggregates.pending_review_count || 0} {(aggregates.pending_review_count || 0) === 1 ? 'request' : 'requests'}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-emerald-100 p-3 sm:p-4">
          <p className="text-xs text-gray-500">Pay to agents</p>
          <p className="text-lg sm:text-xl font-semibold text-emerald-800 tabular-nums">
            {fullCurrency(payToAgents)}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">
            {owesAgents.length} {owesAgents.length === 1 ? 'agent' : 'agents'}
          </p>
        </div>
      </div>

      {tab === 'company_paid' && companyOwes && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-emerald-950">
              Company owes {selectedAgentName || 'the agent'} {fullCurrency(companyOwesAmount)}
            </p>
            <p className="text-xs text-emerald-800 mt-1">
              {agentId
                ? 'Pay this here. Upload the payment proof. It shows as Payment paid on the Daily Branch Report.'
                : 'Select the agent in the Agent filter. The payment box opens for that amount.'}
            </p>
          </div>
          <Button variant="primary" icon={Wallet} onClick={openPay} disabled={!agentId}>
            Pay {fullCurrency(companyOwesAmount)}
          </Button>
        </div>
      )}

      {tab === 'company_paid' ? (
      <div className="mb-4 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">Pay to agents</h2>
            <p className="text-xs text-gray-500 mt-1">
              Agents the company must pay. The Hub and Agent filters above apply here. Pay with a proof image. The agent sees that image under Company paid.
            </p>
          </div>
          <p className="text-sm font-semibold text-emerald-800 tabular-nums">
            {owesAgents.length} {owesAgents.length === 1 ? 'agent' : 'agents'}
          </p>
        </div>
        {loading ? (
          <Loader fullScreen={false} message="Loading agents..." />
        ) : !owesAgents.length ? (
          <p className="text-sm text-gray-400 text-center py-8">No agent to pay in this period</p>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold">Agent</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Hub / Branch</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Amount</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {owesSlice.map((row) => (
                    <Fragment key={row.user_id}>
                      <tr>
                        <td className="px-4 py-2.5 font-medium text-gray-800">{row.name || row.user_id}</td>
                        <td className="px-4 py-2.5 text-gray-600">{row.location_hub_name || '—'}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-emerald-800">{fullCurrency(row.amount)}</td>
                        <td className="px-4 py-2.5 text-right">
                          <Button variant="primary" icon={Wallet} onClick={() => openPayFor(row)}>
                            Pay {fullCurrency(row.amount)}
                          </Button>
                        </td>
                      </tr>
                      {(row.lines || []).length > 0 ? (
                        <tr>
                          <td colSpan={4} className="px-4 pb-3 pt-0">
                            <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                              {(row.lines || []).map((line) => (
                                <div key={line.id || line.ml_sheet_id} className="flex items-center justify-between gap-3 text-xs text-gray-600 py-1">
                                  <span>{line.ml_sheet_id || 'Record'}{line.created_at ? ` · ${formatIstDateTime(line.created_at)}` : ''}</span>
                                  <span className="tabular-nums font-medium text-gray-800">{fullCurrency(line.amount)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden divide-y divide-gray-100">
              {owesSlice.map((row) => (
                <div key={row.user_id} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-800">{row.name || row.user_id}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{row.location_hub_name || '—'}</p>
                      <p className="text-sm font-semibold text-emerald-800 tabular-nums mt-1">{fullCurrency(row.amount)}</p>
                    </div>
                    <Button variant="primary" icon={Wallet} onClick={() => openPayFor(row)}>Pay</Button>
                  </div>
                  {(row.lines || []).length > 0 ? (
                    <div className="mt-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                      {(row.lines || []).map((line) => (
                        <div key={line.id || line.ml_sheet_id} className="flex items-center justify-between gap-3 text-xs text-gray-600 py-1">
                          <span>{line.ml_sheet_id || 'Record'}</span>
                          <span className="tabular-nums font-medium text-gray-800">{fullCurrency(line.amount)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
            <ListPagination
              currentPage={owesPageSafe}
              totalPages={owesPageCount}
              onNext={() => setOwesPage((page) => page + 1)}
            />
          </>
        )}
      </div>
      ) : null}

      {tab === 'company_paid' ? (
        <div className="mb-4 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-800">
              Company paid {selectedAgentName || 'agents'}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Payments already sent from this page. The agent sees the same track under Company paid.
            </p>
          </div>
          {loading ? (
            <Loader fullScreen={false} message="Loading payments..." />
          ) : !companyPayments.length ? (
            <p className="text-sm text-gray-400 text-center py-8">No company payment in this period</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold">Payment</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Agent</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Mode</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Paid by</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Amount</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Proof</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {companyPayments.map((row) => (
                    <Fragment key={row.expense_id}>
                      <tr>
                        <td className="px-4 py-2.5 font-mono text-xs">{row.expense_id}</td>
                        <td className="px-4 py-2.5">{row.paid_to || row.paid_user_id}</td>
                        <td className="px-4 py-2.5 text-gray-600">{formatIstDateTime(row.payment_date) || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-600 capitalize">{row.payment_mode || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-600">{row.paid_by || '—'}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-emerald-800">{fullCurrency(row.amount)}</td>
                        <td className="px-4 py-2.5 text-right">
                          {row.attachment_url ? (
                            <button
                              type="button"
                              className="text-sky-800 underline text-xs font-semibold"
                              onClick={() => setProofView(row)}
                            >
                              View proof
                            </button>
                          ) : '—'}
                        </td>
                      </tr>
                      {(row.lines || []).length > 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 pb-3 pt-0">
                            <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                              {(row.lines || []).map((line) => (
                                <div key={line.id || line.ml_sheet_id} className="flex items-center justify-between gap-3 text-xs text-gray-600 py-1">
                                  <span>{line.ml_sheet_id || 'Record'}</span>
                                  <span className="tabular-nums font-medium text-gray-800">{fullCurrency(line.amount)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {tab !== 'company_paid' ? (
      <>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs sm:text-sm">
        <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-800 border border-blue-100 px-3 py-1 font-semibold">
          {rows.length} {rows.length === 1 ? 'request' : 'requests'} in {tabLabel.toLowerCase()}
        </span>
        <span className="text-gray-600">
          Period total {totalCount}
          <span className="text-gray-400"> · </span>
          Pending {counts.pending || 0}
          <span className="text-gray-400"> · </span>
          Approved {counts.approved || 0}
          <span className="text-gray-400"> · </span>
          Rejected {counts.rejected || 0}
        </span>
      </div>

      {loading ? (
        <Loader fullScreen={false} message="Loading requests..." />
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {!rows.length ? (
            <p className="text-sm text-gray-400 text-center py-10">No requests in this tab</p>
          ) : (
            <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-[860px] w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold whitespace-nowrap sticky left-0 bg-gray-50 z-10">Batch</th>
                    <th className="text-left px-4 py-2.5 font-semibold whitespace-nowrap">Hub / Branch</th>
                    <th className="text-left px-4 py-2.5 font-semibold whitespace-nowrap">Agent</th>
                    <th className="text-left px-4 py-2.5 font-semibold whitespace-nowrap">Status</th>
                    <th className="text-right px-4 py-2.5 font-semibold whitespace-nowrap">Due to company</th>
                    <th className="text-right px-4 py-2.5 font-semibold whitespace-nowrap">Paid now</th>
                    <th className="text-right px-4 py-2.5 font-semibold whitespace-nowrap">Paid remittance</th>
                    <th className="text-right px-4 py-2.5 font-semibold whitespace-nowrap">To-Pay remittance</th>
                    <th className="text-right px-4 py-2.5 font-semibold whitespace-nowrap">Total</th>
                    <th className="text-left px-4 py-2.5 font-semibold whitespace-nowrap">
                      {tab === 'rejected' ? 'Rejected by' : 'Approved by'}
                    </th>
                    <th className="text-right px-4 py-2.5 font-semibold whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row) => (
                    <Fragment key={row.batch_id}>
                      <tr className="hover:bg-sky-50/40 cursor-pointer" onClick={() => openView(row)}>
                        <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap sticky left-0 bg-white">
                          <button type="button" className="underline" onClick={() => openView(row)}>
                            {row.batch_id}
                          </button>
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap">{row.location_hub_name || '—'}</td>
                        <td className="px-4 py-2.5">
                          <p className="font-medium text-gray-800 whitespace-nowrap">{row.agent_name}</p>
                          <p className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap">
                            {formatIstDateTime(row.submitted_at) || '—'}
                          </p>
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 rounded-full border text-xs ${statusBadge(row.status)}`}>
                            {statusLabel(row.status)}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">{fullCurrency(row.due_to_company)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">{fullCurrency(row.amount_paid ?? row.total_amount)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">{fullCurrency(row.paid_amount)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">{fullCurrency(row.to_pay_amount)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-medium whitespace-nowrap">{fullCurrency(row.total_amount)}</td>
                        <td className="px-4 py-2.5">
                          {reviewerLabel(row) ? (
                            <>
                              <p className="font-medium text-gray-800 whitespace-nowrap">{reviewerLabel(row)}</p>
                              <p className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap">
                                {formatIstDateTime(approvedAtValue(row)) || '—'}
                              </p>
                            </>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                          <RowActionsMenu
                            className="relative menu-container inline-flex"
                            isOpen={openMenuId === row.batch_id}
                            onToggle={() => setOpenMenuId(openMenuId === row.batch_id ? null : row.batch_id)}
                            onClose={() => setOpenMenuId(null)}
                            onView={() => openView(row)}
                            extraItems={canReview(row.status) && busyId !== row.batch_id ? [
                              {
                                key: 'approve',
                                icon: '✅',
                                label: 'Approve',
                                variant: 'success',
                                onClick: () => onApprove(row.batch_id),
                              },
                              {
                                key: 'reject',
                                icon: '❌',
                                label: 'Reject',
                                variant: 'danger',
                                onClick: () => onReject(row.batch_id),
                              },
                            ] : []}
                          />
                        </td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden space-y-3 p-3 sm:p-4">
              {rows.map((row) => (
                <div
                  key={`card-${row.batch_id}`}
                  className="rounded-xl border border-gray-100 border-l-4 border-l-blue-500 bg-white p-3.5 shadow-sm cursor-pointer"
                  onClick={() => openView(row)}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <button
                      type="button"
                      className={`${listIdLinkMobileClass} text-left`}
                      onClick={(e) => { e.stopPropagation(); openView(row); }}
                    >
                      {row.batch_id}
                    </button>
                    <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-semibold ${statusBadge(row.status)}`}>
                        {statusLabel(row.status)}
                      </span>
                      <RowActionsMenu
                        className="relative menu-container"
                        isOpen={openMenuId === `card-${row.batch_id}`}
                        onToggle={() => setOpenMenuId(openMenuId === `card-${row.batch_id}` ? null : `card-${row.batch_id}`)}
                        onClose={() => setOpenMenuId(null)}
                        onView={() => openView(row)}
                        extraItems={canReview(row.status) && busyId !== row.batch_id ? [
                          {
                            key: 'approve',
                            icon: '✅',
                            label: 'Approve',
                            variant: 'success',
                            onClick: () => onApprove(row.batch_id),
                          },
                          {
                            key: 'reject',
                            icon: '❌',
                            label: 'Reject',
                            variant: 'danger',
                            onClick: () => onReject(row.batch_id),
                          },
                        ] : []}
                      />
                    </div>
                  </div>
                  <p className="text-sm font-medium text-gray-800 break-words">{row.agent_name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{formatIstDateTime(row.submitted_at) || '—'}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{row.location_hub_name || '—'}</p>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                    <div>
                      <span className="text-gray-500 block">Due to company</span>
                      <p className="tabular-nums font-medium">{fullCurrency(row.due_to_company)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Paid now</span>
                      <p className="tabular-nums font-medium">{fullCurrency(row.amount_paid ?? row.total_amount)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Paid</span>
                      <p className="tabular-nums font-medium">{fullCurrency(row.paid_amount)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 block">To-Pay</span>
                      <p className="tabular-nums font-medium">{fullCurrency(row.to_pay_amount)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Total</span>
                      <p className="tabular-nums font-semibold">{fullCurrency(row.total_amount)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 block">{tab === 'rejected' ? 'Rejected by' : 'Approved by'}</span>
                      <p className="font-medium text-gray-800">{reviewerLabel(row) || '—'}</p>
                      <p className="text-gray-500">{formatIstDateTime(approvedAtValue(row)) || '—'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      )}
      </>
      ) : null}

      {payOpen ? (
        <SettlementModalShell
          title="Pay agent"
          subtitle="Company cash to this agent. Upload the payment proof. This shows as Payment paid on the Daily Branch Report."
          size="md"
          onClose={() => { if (!paying) { setPayOpen(false); setPayTarget(null); } }}
          footer={(
            <div className="flex justify-end gap-2 flex-wrap">
              <Button variant="secondary" disabled={paying} onClick={() => { setPayOpen(false); setPayTarget(null); }}>Cancel</Button>
              <Button
                variant="primary"
                icon={paying ? undefined : Wallet}
                disabled={paying}
                onClick={async () => {
                  const targetAgent = payTarget?.user_id || agentId;
                  const target = Number(payTarget?.amount ?? Math.abs(Number(aggregates.due_to_company) || 0));
                  if (!targetAgent) {
                    Alert.error('Select the agent in the filter, then pay');
                    return;
                  }
                  if (!(target > 0)) {
                    Alert.error('This agent has nothing for the company to pay');
                    return;
                  }
                  if (!payReceipt) {
                    Alert.error('Payment proof image is required');
                    return;
                  }
                  try {
                    setPaying(true);
                    const listed = await mlAgentCommissionService.listUnpaid({ agent_user_id: targetAgent });
                    const unpaid = listed?.data || [];
                    const rowAmount = (row) => Number(row.net_amount != null ? row.net_amount : row.commission_amount) || 0;
                    const rows = unpaid
                      .map((row) => ({ id: row.id, amt: rowAmount(row) }))
                      .filter((row) => row.amt > 0);
                    const unpaidTotal = rows.reduce((sum, row) => sum + row.amt, 0);
                    const chosenIds = (payTarget?.entry_ids || []).map((id) => Number(id)).filter((id) => id);
                    let ids = [];
                    if (chosenIds.length) {
                      const chosen = new Set(chosenIds);
                      const picked = rows.filter((row) => chosen.has(Number(row.id)));
                      const pickedTotal = picked.reduce((sum, row) => sum + row.amt, 0);
                      if (picked.length && Math.abs(pickedTotal - target) <= 1) {
                        ids = picked.map((row) => row.id);
                      }
                    } else if (Math.abs(unpaidTotal - target) <= 1) {
                      ids = rows.map((row) => row.id);
                    } else if (rows.length <= 16) {
                      const limit = 1 << rows.length;
                      for (let mask = 1; mask < limit; mask += 1) {
                        let sum = 0;
                        const picked = [];
                        for (let i = 0; i < rows.length; i += 1) {
                          if (mask & (1 << i)) {
                            sum += rows[i].amt;
                            picked.push(rows[i].id);
                          }
                        }
                        if (Math.abs(sum - target) <= 1) {
                          ids = picked;
                          break;
                        }
                      }
                    }
                    if (!ids.length) {
                      Alert.error(
                        rows.length
                          ? 'Unpaid commission rows do not add up to this amount, so nothing was paid'
                          : 'No unpaid commission found for this agent',
                      );
                      return;
                    }
                    const form = new FormData();
                    form.append('entry_ids', JSON.stringify(ids));
                    form.append('payment_mode', payMode);
                    form.append('remarks', payRemarks);
                    form.append('receipt', payReceipt);
                    const res = await mlAgentCommissionService.pay(form);
                    Alert.success(res?.message || 'Payment recorded');
                    setPayOpen(false);
                    setPayTarget(null);
                    await loadData();
                  } catch (err) {
                    Alert.error(err.response?.data?.error || 'Payment failed');
                  } finally {
                    setPaying(false);
                  }
                }}
              >
                {paying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                {paying ? 'Paying…' : 'Pay and settle'}
              </Button>
            </div>
          )}
        >
          <div className="space-y-4">
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-3">
              <p className="text-xs text-emerald-800">Amount the company pays</p>
              <p className="text-lg font-semibold text-emerald-900 tabular-nums">
                {fullCurrency(Number(payTarget?.amount ?? Math.abs(Number(aggregates.due_to_company) || 0)))}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {payTarget?.name
                  || (agentOptions.find((a) => String(a.user_id) === String(payTarget?.user_id || agentId)) || {}).name
                  || payTarget?.user_id
                  || agentId}
              </p>
              {(payTarget?.lines || []).length > 1 ? (
                <div className="mt-2 border-t border-emerald-100 pt-2">
                  {(payTarget.lines || []).map((line) => (
                    <div key={line.id || line.ml_sheet_id} className="flex items-center justify-between gap-3 text-xs text-emerald-900 py-0.5">
                      <span>{line.ml_sheet_id || 'Record'}</span>
                      <span className="tabular-nums">{fullCurrency(line.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Payment mode</label>
              <select
                value={payMode}
                onChange={(e) => setPayMode(e.target.value)}
                className={formSelectClass()}
              >
                {PAYMENT_MODES.map((mode) => (
                  <option key={mode.value} value={mode.value}>{mode.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
              <input
                value={payRemarks}
                onChange={(e) => setPayRemarks(e.target.value)}
                className={formInputClass()}
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Payment proof image</label>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const okTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
                  if (!okTypes.includes(file.type)) {
                    Alert.error('Please upload a JPG or PNG image');
                    return;
                  }
                  setPayReceipt(file);
                }}
                className="block w-full text-sm text-gray-700"
              />
              {payReceipt ? (
                <p className="text-xs text-gray-500 mt-1">{payReceipt.name}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">JPG or PNG of the payment.</p>
              )}
            </div>
          </div>
        </SettlementModalShell>
      ) : null}

      {rejectTarget ? (
        <SettlementModalShell
          title="Reject settlement"
          subtitle={rejectTarget.batchId}
          size="md"
          zIndex={100}
          onClose={closeReject}
          footer={(
            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="secondary" size="md" onClick={closeReject} disabled={Boolean(busyId)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                size="md"
                loading={busyId === rejectTarget.batchId}
                onClick={confirmReject}
              >
                Reject settlement
              </Button>
            </div>
          )}
        >
          <p className="text-sm text-gray-600 mb-3">
            This request will be marked rejected. You can add a note for the agent.
          </p>
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="settlement-reject-notes">
            Rejection notes
          </label>
          <textarea
            id="settlement-reject-notes"
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            rows={4}
            maxLength={500}
            autoFocus
            placeholder="Optional — reason for rejection"
            className={`${formInputClass()} min-h-[96px] resize-y`}
          />
          <p className="text-[11px] text-gray-400 mt-1 text-right">{rejectNotes.length}/500</p>
        </SettlementModalShell>
      ) : null}

      {viewBatch ? (
        <CompanySettlementBatchView
          batch={viewBatch}
          onClose={() => setViewBatch(null)}
          canReview={canReview(viewBatch.status)}
          busy={busyId === viewBatch.batch_id}
          onApprove={onApprove}
          onReject={onReject}
        />
      ) : null}

      {proofView ? (
        <PaymentProofModal
          url={proofView.attachment_url}
          title={proofView.expense_id || 'Payment proof'}
          onClose={() => setProofView(null)}
        />
      ) : null}
    </DashboardLayout>
  );
};

export default AccountantCompanySettlementApprovals;
